import React, { useEffect, useState } from 'react'
import styled from '@emotion/styled'
import { number, object, oneOfType, string } from 'prop-types'
import {
	generateAffiliateLink,
	getParagraphSections,
	makeImgResizerUrl,
	replaceSpacesWithDashes,
	splitParagraphString,
} from '@jelly/utils'

import { AnchorTag, Chevron, MonetizationButton } from '@jelly/components'

const TabBodyContainer = styled.div`
	display: block;
	height: auto;
	padding: 30px;
	width: 100%;

	${({ theme }) => theme.MQ.M} {
		display: flex;
		justify-content: space-between;
	}
`
const ThumbnailWrapper = styled.div`
	color: inherit;
`
const Thumbnail = styled.img`
	display: inline-block;
	height: auto;
	max-width: 150px;
`
const BestPicksContext = styled.div`
	display: inline-block;

	${({ theme }) => theme.MQ.M} {
		margin-left: 30px;
		width: 60%;
	}
`
const ServiceName = styled.div`
	color: inherit;
	font-size: 20px;
`
const FullTitle = styled.div`
	color: ${({ theme }) => theme.colors.darkGrey};
`
const Description = styled.div`
	margin-top: 20px;
`
const TopSection = styled.div`
	display: block;
`
const ShowMoreButton = styled.div`
	color: ${({ theme }) => theme.colors.contentLink};
	cursor: pointer;
	display: inline-block;
`
const ChevronContainer = styled.div`
	${({ showBottom }) => !showBottom && `margin-right: 8px;`}
	display: inline-block;
	margin-left: 8px;
	margin-top: 3px;

	polyline {
		stroke: ${({ theme }) => theme.colors.contentLink};
	}
`
const BottomSection = styled.div`
	display: ${({ showBottom }) => (showBottom ? `block` : `none`)};
`
const ButtonWrapper = styled.div`
	width: 100%;

	button {
		margin-right: 0;
		margin-left: auto;
		right: 0;
	}

	${({ theme }) => theme.MQ.M} {
		width: 25%;
		button {
			letter-spacing: normal;
		}
	}
`

TabBody.propTypes = {
	bestPickDescription: string,
	categoryWidgetId: string,
	canonical: string.isRequired,
	company: object,
	fullTitle: string,
	id: oneOfType([number, string]),
	label: string,
	pricing: object.isRequired,
	serviceName: string,
	thumbnail: string,
	url: string.isRequired,
}

export function TabBody({ categoryWidgetId, company, description, fullTitle, id, label, review, url }) {
	const { canonical, fullReview, pricing, serviceName, thumbnail } = !!review ? review : {}
	const [paragraphs, setParagraphs] = useState()
	const [topSection, setTopSection] = useState()
	const [bottomSection, setBottomSection] = useState()
	const [showBottom, setShowBottom] = useState(false)
	const serviceLink = fullReview ? canonical : generateAffiliateLink(pricing)

	useEffect(() => {
		description && setParagraphs(splitParagraphString(description))
		setShowBottom(false)
	}, [description]) //eslint-disable-line

	useEffect(() => {
		paragraphs && setTopSection(getParagraphSections(paragraphs, 0, 2))
		paragraphs && setBottomSection(getParagraphSections(paragraphs, 3, paragraphs.length))
	}, [paragraphs])

	return (
		<TabBodyContainer>
			<ThumbnailWrapper {...(serviceLink && { alt: serviceName, as: AnchorTag, href: serviceLink })}>
				<Thumbnail alt={serviceName} src={makeImgResizerUrl({ src: thumbnail?.path, method: 'w', w: 150 })} />
			</ThumbnailWrapper>
			<BestPicksContext>
				<ServiceName {...(serviceLink && { as: AnchorTag, href: canonical })}>{serviceName}</ServiceName>
				<FullTitle>{fullTitle}</FullTitle>
				<Description>
					<TopSection dangerouslySetInnerHTML={{ __html: topSection }} />
					{bottomSection && (
						<div>
							<ShowMoreButton
								id={`category_${replaceSpacesWithDashes(serviceName)}_bestpicks_readmore`}
								onClick={() => setShowBottom(!showBottom)}
							>
								{showBottom ? `Hide` : `Read More`}
								<ChevronContainer showBottom={showBottom}>
									<Chevron height={15} width={15} rotationDeg={!showBottom ? 180 : 0} />
								</ChevronContainer>
							</ShowMoreButton>
							<BottomSection
								dangerouslySetInnerHTML={{ __html: bottomSection }}
								showBottom={showBottom}
							/>
						</div>
					)}
				</Description>
			</BestPicksContext>
			<ButtonWrapper>
				<MonetizationButton
					categoryWidgetId={categoryWidgetId}
					pricing={pricing}
					rank={id}
					serviceName={serviceName}
					sectionType="bestpicks"
					tabTitle={label}
					templateType="category"
					type="bestPick"
				/>
			</ButtonWrapper>
		</TabBodyContainer>
	)
}
